import React, { FC, useCallback, useState } from "react";
import { Checkbox, Dialog, ButtonProps } from "@xapp/components";

import { BasicIntegrationCard, IntegrationCardProp } from "./BasicIntegrationCard";
import { ServiceTitanConnectDialog, ServiceTitanConnectionProps } from "./ServiceTitanConnectDialog";

export type { ServiceTitanConnectionProps } from "./ServiceTitanConnectDialog";

export interface ServiceTitanSettingsUpdate {
    leadImport?: boolean;
    outcomeTracking?: boolean;
}

export interface ServiceTitanIntegrationCardProps extends IntegrationCardProp {
    /**
     * Whether lead import is currently enabled for a linked integration.
     */
    leadImport?: boolean;
    /**
     * Whether outcome tracking is currently enabled for a linked integration.
     */
    outcomeTracking?: boolean;
    /**
     * Whether the tenant has accepted v2 of our ServiceTitan app, which outcome tracking
     * requires. Undefined means unknown and is treated as accepted; explicitly false disables
     * the outcome tracking toggle and surfaces the not-yet-upgraded state instead of letting
     * it silently do nothing.
     */
    appV2Accepted?: boolean;
    /**
     * Connect ServiceTitan with the full configuration collected by the connect dialog.
     */
    onConnect?(props: ServiceTitanConnectionProps): void;
    /**
     * Update the ingestion settings of an already linked integration.
     */
    onUpdateSettings?(settings: ServiceTitanSettingsUpdate): void;
}

const helperStyle: React.CSSProperties = { color: "#666", fontSize: "12px", marginTop: "4px" };

export const ServiceTitanIntegrationCard: FC<ServiceTitanIntegrationCardProps> = (props) => {
    const {
        appId,
        checked,
        disabled,
        isLinked,
        isActive,
        leadImport,
        outcomeTracking,
        appV2Accepted,
        onConnect,
        onDisconnect,
        onUpdateSettings,
    } = props;

    const [showDialog, setShowDialog] = useState<boolean>(false);

    const handleServiceTitanClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
        e.preventDefault();

        if (typeof checked === "boolean") {
            if (typeof props.onClick === "function") {
                props.onClick(e);
            }
        } else {
            // pop up modal
            setShowDialog(true);
        }
    };

    const handleDisconnectServiceTitan = useCallback(() => {
        if (typeof onDisconnect === "function") {
            onDisconnect();
            setShowDialog(false);
        }
    }, [onDisconnect]);

    const settingsActions: ButtonProps[] = [
        {
            label: "Close",
            variation: "secondary",
            onClick: () => {
                setShowDialog(false);
            },
        },
    ];

    return (
        <>
            <BasicIntegrationCard
                checked={checked}
                disabled={disabled}
                imageUrl="/img/integrations/servicetitan/service-titan-logo.png"
                integrationId="servicetitan"
                text="ServiceTitan"
                href={() => "/integrations/servicetitan"}
                onClick={handleServiceTitanClick}
                isLinked={isLinked}
                isActive={isActive}
                onDisconnect={typeof onDisconnect === "function" ? handleDisconnectServiceTitan : undefined}
            />
            {isLinked ? (
                <Dialog
                    active={showDialog}
                    actions={settingsActions}
                    title="ServiceTitan Settings"
                    onClose={() => {
                        setShowDialog(false);
                    }}
                >
                    <div style={{ display: "flex", flexDirection: "column", rowGap: "10px" }}>
                        <div>
                            <Checkbox
                                checked={!!leadImport}
                                label="Lead import"
                                onChange={(value) => {
                                    if (typeof onUpdateSettings === "function") {
                                        onUpdateSettings({ leadImport: value });
                                    }
                                }}
                            />
                            <p style={helperStyle}>
                                Pull leads and bookings created in ServiceTitan into Studio, alongside any email-forward
                                ingestion you already have. Nothing changes about how we deliver your leads to
                                ServiceTitan — this is safe to leave off.
                            </p>
                        </div>
                        <div>
                            <Checkbox
                                checked={!!outcomeTracking}
                                disabled={appV2Accepted === false}
                                label="Outcome tracking"
                                onChange={(value) => {
                                    if (appV2Accepted !== false && typeof onUpdateSettings === "function") {
                                        onUpdateSettings({ outcomeTracking: value });
                                    }
                                }}
                            />
                            <p style={helperStyle}>
                                Follow the leads we send to ServiceTitan through to job completion and invoiced and
                                collected totals. This is safe to leave off.
                            </p>
                            {appV2Accepted === false && (
                                <p
                                    data-testid="st-outcome-tracking-upgrade"
                                    style={{ color: "#b26a00", fontSize: "12px", marginTop: "4px" }}
                                >
                                    Outcome tracking requires v2 of our ServiceTitan app, which your ServiceTitan
                                    account has not accepted yet. Accept the app upgrade in ServiceTitan and this toggle
                                    will become available.
                                </p>
                            )}
                        </div>
                    </div>
                </Dialog>
            ) : (
                <ServiceTitanConnectDialog
                    appId={appId}
                    active={showDialog}
                    onClose={() => {
                        setShowDialog(false);
                    }}
                    onConnect={onConnect}
                />
            )}
        </>
    );
};
